import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ThemeProvider } from "@/context/ThemeContext";
import { Toaster } from "sonner";

// Public
import PublicLayout from "@/components/layout/PublicLayout";
import HomePage from "@/pages/public/HomePage";
import AboutPage from "@/pages/public/AboutPage";
import ServicesPage from "@/pages/public/ServicesPage";
import ServiceDetailPage from "@/pages/public/ServiceDetailPage";
import ProjectsPage from "@/pages/public/ProjectsPage";
import ProjectDetailPage from "@/pages/public/ProjectDetailPage";
import TendersPage from "@/pages/public/TendersPage";
import DownloadsPage from "@/pages/public/DownloadsPage";
import ContactPage from "@/pages/public/ContactPage";
import GrievancePage from "@/pages/public/GrievancePage";
import RTIPage from "@/pages/public/RTIPage";
import TrackApplicationPage from "@/pages/public/TrackApplicationPage";
import SearchPage from "@/pages/public/SearchPage";
import GISDashboardPage from "@/pages/public/GISDashboardPage";

// Auth
import LoginPage from "@/pages/auth/LoginPage";
import RegisterPage from "@/pages/auth/RegisterPage";

// App shell
import AppShell from "@/components/layout/AppShell";
import ProtectedRoute from "@/components/ProtectedRoute";
import ExecutiveDashboard from "@/pages/app/ExecutiveDashboard";
import CitizenDashboard from "@/pages/app/CitizenDashboard";
import WorksPage from "@/pages/app/WorksPage";
import FinancePage from "@/pages/app/FinancePage";
import HRPage from "@/pages/app/HRPage";
import InventoryPage from "@/pages/app/InventoryPage";
import AdministrationPage from "@/pages/app/AdministrationPage";
import KnowledgePage from "@/pages/app/KnowledgePage";
import LearningPage from "@/pages/app/LearningPage";
import ITAssetsPage from "@/pages/app/ITAssetsPage";
import InspectionPage from "@/pages/app/InspectionPage";
import GrievancesRTIAdmin from "@/pages/app/GrievancesRTIAdmin";
import SecurityPage from "@/pages/app/SecurityPage";
import EmployeeProfilePage from "@/pages/app/EmployeeProfilePage";

function RootRedirect() {
  const { user } = useAuth();
  if (user === null) return null;
  const isCitizen = ["citizen","industry","farmer","vendor","consultant"].includes(user?.role);
  return <Navigate to={isCitizen ? "/app/citizen" : "/app/dashboard"} replace />;
}

function App() {
  return (
    <div className="App font-body">
      <ThemeProvider>
        <BrowserRouter>
          <AuthProvider>
            <Toaster richColors position="top-right" />
            <Routes>
              {/* Public */}
              <Route element={<PublicLayout />}>
                <Route path="/" element={<HomePage />} />
                <Route path="/about" element={<AboutPage />} />
                <Route path="/services" element={<ServicesPage />} />
                <Route path="/services/:scheme" element={<ServiceDetailPage />} />
                <Route path="/projects" element={<ProjectsPage />} />
                <Route path="/projects/:id" element={<ProjectDetailPage />} />
                <Route path="/tenders" element={<TendersPage />} />
                <Route path="/downloads" element={<DownloadsPage />} />
                <Route path="/contact" element={<ContactPage />} />
                <Route path="/grievance" element={<GrievancePage />} />
                <Route path="/rti" element={<RTIPage />} />
                <Route path="/track" element={<TrackApplicationPage />} />
                <Route path="/search" element={<SearchPage />} />
                <Route path="/gis" element={<GISDashboardPage />} />
              </Route>

              {/* Auth */}
              <Route path="/login" element={<LoginPage />} />
              <Route path="/register" element={<RegisterPage />} />

              {/* App shell (protected) */}
              <Route path="/app" element={<ProtectedRoute><AppShell /></ProtectedRoute>}>
                <Route index element={<RootRedirect />} />
                <Route path="dashboard" element={<ExecutiveDashboard />} />
                <Route path="citizen" element={<CitizenDashboard />} />
                <Route path="works" element={<WorksPage />} />
                <Route path="inspection" element={<InspectionPage />} />
                <Route path="gis" element={<GISDashboardPage />} />
                <Route path="erp/finance" element={<FinancePage />} />
                <Route path="erp/hr" element={<HRPage />} />
                <Route path="erp/inventory" element={<InventoryPage />} />
                <Route path="erp/admin" element={<AdministrationPage />} />
                <Route path="knowledge" element={<KnowledgePage />} />
                <Route path="learning" element={<LearningPage />} />
                <Route path="it-assets" element={<ITAssetsPage />} />
                <Route path="tenders" element={<TendersPage />} />
                <Route path="grievances" element={<GrievancesRTIAdmin />} />
                <Route path="security" element={<SecurityPage />} />
                <Route path="profile" element={<EmployeeProfilePage />} />
              </Route>

              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </AuthProvider>
        </BrowserRouter>
      </ThemeProvider>
    </div>
  );
}

export default App;
